import type {
  FileWithUri,
  OpenNativeCameraMessage,
  OpenNativeStoragePickerMessage,
  OpenNativeToastMessage,
  PhotoFromNativeDetail,
} from 'app/pages/mobile/pages/mobile-transactions/__types__/MobileOptionMenu.types';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

const globalState = new Map<
  string,
  {
    imgPreview: string | undefined;
    selectedFiles: FileWithUri[];
  }
>();

// Error logging utility
const logError = (error: unknown, context: string, scopeId?: string) => {
  const errorMessage = error instanceof Error ? error.message : 'Unknown error';
  const errorObject = error instanceof Error ? error : new Error(String(error));

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.error(
      `[useMobileResourcePicker] Error in ${context}${scopeId ? ` (scope: ${scopeId})` : ''}:`,
      errorObject
    );
  } else {
    // eslint-disable-next-line no-console
    console.error(`[useMobileResourcePicker] ${context}: ${errorMessage}`);
    Sentry.captureException(errorObject, {
      tags: { component: 'useMobileResourcePicker' },
      extra: { context, scopeId },
    });
  }
};

type NativeBridgeWindow = Window & {
  ReactNativeWebView?: { postMessage: (message: string) => void };
};

// The React Native WebView always injects window.ReactNativeWebView. When it is
// absent we are running the mobile UI in a plain browser (for example, spoofing
// the SpeedchainMobile user agent for testing), so there is no native layer to
// open the camera or file pickers.
const getNativeBridge = (): NativeBridgeWindow['ReactNativeWebView'] =>
  typeof window !== 'undefined' ? (window as NativeBridgeWindow).ReactNativeWebView : undefined;

// Browser fallback for the native pickers. Opens a real <input type="file"> and
// re-emits the exact CustomEvent the native layer would, so the rest of the
// picker pipeline (the *_FROM_NATIVE listeners) is unchanged. This never runs
// inside the app's WebView because getNativeBridge() is always defined there,
// which keeps the on-device experience untouched.
const openBrowserFilePicker = ({
  accept,
  capture,
  scopeId,
  toEvent,
  onError,
}: {
  accept: string;
  capture?: 'user' | 'environment';
  scopeId?: string;
  toEvent: (dataUrl: string, file: File) => { name: string; detail: Record<string, unknown> };
  onError: (error: unknown) => void;
}): void => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  if (capture) input.setAttribute('capture', capture);
  input.style.display = 'none';

  const cleanup = () => input.remove();

  input.addEventListener('cancel', cleanup);
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) {
      cleanup();
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { name, detail } = toEvent(reader.result as string, file);
        window.dispatchEvent(new CustomEvent(name, { detail: { ...detail, scopeId } }));
      } catch (error) {
        onError(error);
      } finally {
        cleanup();
      }
    };
    reader.onerror = () => {
      onError(reader.error);
      cleanup();
    };
    reader.readAsDataURL(file);
  });

  document.body.appendChild(input);
  input.click();
};

export const useMobileResourcePicker = (scopeId?: string) => {
  const key = scopeId || 'global';
  const isMountedRef = useRef(true);

  // Initialize state for this scope
  if (!globalState.has(key)) {
    globalState.set(key, {
      imgPreview: undefined,
      selectedFiles: [],
    });
  }

  const [imgPreview, setImgPreview] = useState<string | undefined>(globalState.get(key)?.imgPreview);
  const [selectedFiles, setSelectedFiles] = useState<FileWithUri[]>(
    globalState.get(key)?.selectedFiles || []
  );

  useEffect(() => {
    const photoHandler = (e: Event) => {
      try {
        const eventDetail = (e as CustomEvent<PhotoFromNativeDetail & { scopeId?: string }>).detail ?? {};
        const { dataUrl, scopeId: eventScopeId } = eventDetail;
        // Handle "undefined" string from React Native serialization
        const normalizedEventScope = eventScopeId && eventScopeId !== 'undefined' ? eventScopeId : 'global';
        // If scope is global but we have a specific key, use the key instead
        const effectiveScope =
          normalizedEventScope === 'global' && key !== 'global' ? key : normalizedEventScope;
        const conditionMet = dataUrl !== undefined && effectiveScope === key && isMountedRef.current;

        if (conditionMet) {
          Sentry.addBreadcrumb({
            category: 'camera',
            message: 'received PHOTO_FROM_NATIVE',
            data: { bytes: dataUrl?.length },
          });
          setImgPreview(dataUrl);
          const currentState = globalState.get(key) || {
            imgPreview: undefined,
            selectedFiles: [],
          };
          globalState.set(key, { ...currentState, imgPreview: dataUrl });
        }
      } catch (error) {
        // Enhanced error logging with event payload details
        const eventDetail = (e as CustomEvent<PhotoFromNativeDetail & { scopeId?: string }>).detail ?? {};
        const errorWithContext = new Error(
          `photoHandler error - Event payload: ${JSON.stringify(eventDetail)}, Key: ${key}`
        );
        logError(errorWithContext, 'photoHandler', key);
      }
    };

    const fileHandler = (e: Event) => {
      try {
        const eventDetail = (e as CustomEvent<PhotoFromNativeDetail & { scopeId?: string }>).detail ?? {};
        const { files, scopeId: eventScopeId } = eventDetail;
        // Handle "undefined" string from React Native serialization
        const normalizedEventScope = eventScopeId && eventScopeId !== 'undefined' ? eventScopeId : 'global';
        // If scope is global but we have a specific key, use the key instead
        const effectiveScope =
          normalizedEventScope === 'global' && key !== 'global' ? key : normalizedEventScope;
        const conditionMet = files !== undefined && effectiveScope === key && isMountedRef.current;

        if (conditionMet) {
          setSelectedFiles(files);
          const currentState = globalState.get(key) || {
            imgPreview: undefined,
            selectedFiles: [],
          };
          globalState.set(key, { ...currentState, selectedFiles: files });
        }
      } catch (error) {
        // Enhanced error logging with event payload details
        const eventDetail = (e as CustomEvent<PhotoFromNativeDetail & { scopeId?: string }>).detail ?? {};
        const errorWithContext = new Error(
          `fileHandler error - Event payload: ${JSON.stringify(eventDetail)}, Key: ${key}`
        );
        logError(errorWithContext, 'fileHandler', key);
      }
    };

    const pictureHandler = (e: Event) => {
      try {
        const eventDetail = (e as CustomEvent<PhotoFromNativeDetail & { scopeId?: string }>).detail ?? {};
        const { dataUrl, scopeId: eventScopeId } = eventDetail;
        const normalizedEventScope = eventScopeId && eventScopeId !== 'undefined' ? eventScopeId : 'global';

        const effectiveScope =
          normalizedEventScope === 'global' && key !== 'global' ? key : normalizedEventScope;
        const conditionMet = dataUrl !== undefined && effectiveScope === key && isMountedRef.current;

        if (conditionMet) {
          setImgPreview(dataUrl);
          const currentState = globalState.get(key) || {
            imgPreview: undefined,
            selectedFiles: [],
          };
          globalState.set(key, { ...currentState, imgPreview: dataUrl });
        }
      } catch (error) {
        const eventDetail = (e as CustomEvent<PhotoFromNativeDetail & { scopeId?: string }>).detail ?? {};
        const errorWithContext = new Error(
          `photoHandler error - Event payload: ${JSON.stringify(eventDetail)}, Key: ${key}`
        );
        logError(errorWithContext, 'photoHandler', key);
      }
    };

    window.addEventListener('PHOTO_FROM_NATIVE', photoHandler);
    window.addEventListener('FILES_FROM_NATIVE', fileHandler);
    window.addEventListener('PICTURE_FROM_NATIVE', pictureHandler);

    return () => {
      window.removeEventListener('PHOTO_FROM_NATIVE', photoHandler);
      window.removeEventListener('FILES_FROM_NATIVE', fileHandler);
      window.removeEventListener('PICTURE_FROM_NATIVE', pictureHandler);
    };
  }, [key]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const openCamera = (): void => {
    const msg: OpenNativeCameraMessage & { scopeId?: string } = {
      type: 'OPEN_NATIVE_CAMERA',
      scopeId: key !== 'global' ? key : undefined,
    };
    const bridge = getNativeBridge();
    if (bridge) {
      // A missing matching 'PHOTO_FROM_NATIVE received' breadcrumb after this is
      // the signature of the native camera OOM crash (SEE-1208).
      Sentry.addBreadcrumb({ category: 'camera', message: 'posted OPEN_NATIVE_CAMERA' });
      bridge.postMessage(JSON.stringify(msg));
      return;
    }
    openBrowserFilePicker({
      accept: 'image/*',
      capture: 'environment',
      scopeId: msg.scopeId,
      toEvent: (dataUrl) => ({ name: 'PHOTO_FROM_NATIVE', detail: { dataUrl } }),
      onError: (error) => logError(error, 'openCamera browser fallback', key),
    });
  };

  const openStoragePicker = (): void => {
    const msg: OpenNativeStoragePickerMessage & { scopeId?: string } = {
      type: 'OPEN_NATIVE_STORAGE_PICKER',
      scopeId: key !== 'global' ? key : undefined,
    };
    const bridge = getNativeBridge();
    if (bridge) {
      bridge.postMessage(JSON.stringify(msg));
      return;
    }
    openBrowserFilePicker({
      accept: 'application/pdf',
      scopeId: msg.scopeId,
      toEvent: (dataUrl, file) => ({
        name: 'FILES_FROM_NATIVE',
        detail: {
          files: [{ name: file.name, type: file.type, base64: dataUrl.split(',')[1] ?? '' }],
        },
      }),
      onError: (error) => logError(error, 'openStoragePicker browser fallback', key),
    });
  };

  const openPicturePicker = (): void => {
    const msg = {
      type: 'OPEN_PICTURE_PICKER',
      scopeId: key !== 'global' ? key : undefined,
    };

    const bridge = getNativeBridge();
    if (bridge) {
      bridge.postMessage(JSON.stringify(msg));
      return;
    }
    openBrowserFilePicker({
      accept: 'image/*',
      scopeId: msg.scopeId,
      toEvent: (dataUrl) => ({ name: 'PICTURE_FROM_NATIVE', detail: { dataUrl } }),
      onError: (error) => logError(error, 'openPicturePicker browser fallback', key),
    });
  };

  const displayToast = (message: string, severity: 'SUCCESS' | 'ERROR' | 'WARNING'): void => {
    const msg: OpenNativeToastMessage & { scopeId?: string } = {
      type: 'SHOW_TOAST',
      message,
      severity,
      scopeId: key !== 'global' ? key : undefined,
    };
    getNativeBridge()?.postMessage(JSON.stringify(msg));
  };

  const cleanup = useCallback(() => {
    globalState.delete(key);
    setImgPreview(undefined);
    setSelectedFiles([]);
  }, [key]);

  const clearImgPreview = useCallback(() => {
    setImgPreview(undefined);
    const currentState = globalState.get(key) || {
      imgPreview: undefined,
      selectedFiles: [],
    };
    globalState.set(key, { ...currentState, imgPreview: undefined });
  }, [key]);

  const clearSelectedFiles = useCallback(() => {
    setSelectedFiles([]);
    const currentState = globalState.get(key) || {
      imgPreview: undefined,
      selectedFiles: [],
    };
    globalState.set(key, { ...currentState, selectedFiles: [] });
  }, [key]);

  const mimeFromExt: Record<string, string> = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    heic: 'image/heic',
    webp: 'image/webp',
  };

  const inferMimeFromName = (name?: string): string | undefined => {
    if (!name) return undefined;
    const ext = name.split('.').pop()?.toLowerCase();
    return ext ? mimeFromExt[ext] : undefined;
  };

  const inferMimeFromBase64 = (b64: string, fallback = 'image/jpeg'): string => {
    const head = b64.substring(0, 16);
    if (head.startsWith('JVBERi0')) return 'application/pdf'; // %PDF-
    if (head.startsWith('/9j/')) return 'image/jpeg'; // JPEG
    if (head.startsWith('iVBOR')) return 'image/png'; // PNG
    if (head.startsWith('R0lGOD')) return 'image/gif'; // GIF
    return fallback;
  };

  const isAndroid = (): boolean => {
    if (typeof window === 'undefined') return false;

    if (/SpeedchainMobile\/Android\/1\.0/.test(navigator.userAgent)) {
      return true;
    }

    return /Android/i.test(navigator.userAgent);
  };

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    openCamera,
    openStoragePicker,
    openPicturePicker,
    displayToast,
    imgPreview,
    selectedFiles,
    cleanup,
    clearImgPreview,
    clearSelectedFiles,
    isAndroid,
    inferMimeFromName,
    inferMimeFromBase64,
  };
};
